/**
 * Display labels for the PE page group (Fund Administration, Collateral
 * Monitoring, Configuration).
 *
 * Every map is keyed on a generated union from pe-fund-analytics.openapi.yaml or
 * pe-valuation.openapi.yaml, as a Record over that union, so a value the spec
 * adds fails tsc here until it has a label. Before this module each page carried
 * its own strategy and status lists (mid_market, distressed_debt, secondaries,
 * active, liquidated, hybrid) that the spec enums do not contain, so real funds
 * rendered raw codes and the create form offered values the API rejects.
 */
import type { components } from '@aaimplatform/institutional-types/pe-fund-analytics';
import type { components as valuationComponents } from '@aaimplatform/institutional-types/pe-valuation';

export const FUND_STRATEGY_LABELS: Record<components['schemas']['FundStrategy'], string> = {
  large_cap_buyout: 'Large-Cap Buyout',
  mid_market_buyout: 'Mid-Market Buyout',
  small_cap_buyout: 'Small-Cap Buyout',
  growth_equity: 'Growth Equity',
  venture_capital: 'Venture Capital',
  permanent_capital: 'Permanent Capital / Evergreen',
  co_investment: 'Co-Investment',
  secondary: 'Secondary',
  fund_of_funds: 'Fund of Funds',
  credit: 'Credit',
  real_assets: 'Real Assets',
  infrastructure: 'Infrastructure',
};

export const FUND_STATUS: Record<
  components['schemas']['FundStatus'],
  { label: string; variant: 'success' | 'warning' | 'error' | 'neutral' | 'info' }
> = {
  fundraising: { label: 'Fundraising', variant: 'neutral' },
  investing: { label: 'Investing', variant: 'info' },
  harvesting: { label: 'Harvesting', variant: 'neutral' },
  fully_realized: { label: 'Fully realized', variant: 'neutral' },
  evergreen: { label: 'Evergreen', variant: 'info' },
};

export const SECTOR_FOCUS_LABELS: Record<components['schemas']['SectorFocus'], string> = {
  enterprise_software: 'Enterprise Software',
  healthcare: 'Healthcare',
  financial_services: 'Financial Services',
  education: 'Education',
  government: 'Government',
  insurance: 'Insurance',
  cybersecurity: 'Cybersecurity',
  real_estate_tech: 'Real Estate Technology',
  supply_chain: 'Supply Chain',
  consumer: 'Consumer',
  industrials: 'Industrials',
  energy: 'Energy',
  multi_sector: 'Multi-Sector',
};

export const WATERFALL_LABELS: Record<NonNullable<components['schemas']['PeFund']['waterfallType']>, string> = {
  american: 'American',
  european: 'European',
};

export const LP_INTEREST_TYPE_LABELS: Record<components['schemas']['LpInterest']['interestType'], string> = {
  limited_partner: 'Limited partner',
  co_investment: 'Co-investment',
  carried_interest: 'Carried interest',
  general_partner: 'General partner',
};

export const CAPITAL_ACTIVITY_LABELS: Record<components['schemas']['CapitalActivityType'], string> = {
  capital_call: 'Capital call',
  distribution: 'Distribution',
  recallable_distribution: 'Recallable distribution',
  management_fee: 'Management fee',
  return_of_capital: 'Return of capital',
};

export const RISK_RATING_LABELS: Record<
  valuationComponents['schemas']['CollateralAssessmentResponse']['riskRating'],
  string
> = {
  low: 'Low',
  moderate: 'Moderate',
  elevated: 'Elevated',
  high: 'High',
  very_high: 'Very high',
};

/** Label for a value keyed in one of the maps above; an unmapped value prints as-is. */
export function labelFor<K extends string>(labels: Record<K, string>, value: string | undefined | null): string {
  if (!value) return '--';
  return (labels as Record<string, string>)[value] ?? value;
}

/** Ordered option list for a label map, for a select. */
export function optionsFor<K extends string>(labels: Record<K, string>): Array<{ value: K; label: string }> {
  return (Object.keys(labels) as K[]).map((value) => ({ value, label: labels[value] }));
}
